/**
 * Which of the sign-in gate's three screens an exchange failure deserves.
 *
 * Three, not one, because they need three different actions from the person reading them: ask an
 * administrator, retry, or sign out. Pure so it is testable — `FrappeSessionGate` stays thin
 * enough to review by eye, which matters in a repo with no component-test infrastructure.
 */

export type GateFailure = 'unprovisioned' | 'unreachable' | 'invalid-token'

/** The server refused to mint a session because no enabled Frappe `User` matches (spec §5.1). */
const UNPROVISIONED_EXC = 'BosunUserNotProvisioned'

/**
 * A one-line technical summary of an exchange failure, shown small and muted under the message.
 *
 * The screens above deliberately say nothing a purchaser cannot act on, and that is right — but
 * it also meant the CSRF refresh bug (2026-08-13) had to be diagnosed by reading source and
 * probing the bench by hand, because the one fact that identified it (`exc_type: CSRFTokenError`)
 * never reached the screen. Whoever reads this next should be able to quote the cause without
 * opening DevTools. Returns `null` when there is nothing useful to say, so the caller renders
 * nothing rather than an empty line.
 */
export function describeGateError(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null

  const excType = (err as { excType?: unknown }).excType
  const status = (err as { status?: unknown }).status
  const messages = (err as { messages?: unknown }).messages
  const detail = Array.isArray(messages) && typeof messages[0] === 'string' ? messages[0] : null

  const head = [
    typeof excType === 'string' ? excType : null,
    typeof status === 'number' ? `HTTP ${status}` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ')

  if (head && detail) return `${head} — ${detail}`
  return head || detail || null
}

export function classifyGateError(err: unknown): GateFailure {
  if (!err || typeof err !== 'object') return 'invalid-token'

  if ((err as { excType?: unknown }).excType === UNPROVISIONED_EXC) return 'unprovisioned'

  // A cross-origin request blocked by a missing CORS header does NOT surface as a status —
  // `fetch` rejects with a TypeError and the browser logs the real reason to the console only.
  // Pre-F1 that is the single most likely failure here, so it gets a screen that says so.
  const hasStatus = typeof (err as { status?: unknown }).status === 'number'
  if (!hasStatus) return 'unreachable'

  return 'invalid-token'
}
